import React, { useEffect, useState } from "react";
import myConfig from "../../configs/config";
import { fetchChecklistReports } from "./fieldAppApi";

export function mediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${myConfig.BE_URL}${url}`;
}

export function formatChecklistWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR");
}

function FieldAppChecklistReports({ farmId, t }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoUrl, setPhotoUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await fetchChecklistReports(farmId);
        if (!cancelled) setReports(data);
      } catch (error) {
        if (!cancelled) setReports([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [farmId]);

  if (loading) {
    return <p className="text-muted">{t("Carregando checklists...")}</p>;
  }

  if (!reports.length) {
    return (
      <p className="text-muted">
        {t(
          "Nenhum checklist ao assumir ainda. Quando o operador concluir no app, as fotos e a observação aparecem aqui."
        )}
      </p>
    );
  }

  return (
    <div className="field-app-checklist-reports">
      {reports.map((report) => (
        <article key={report.id} className="field-app-checklist-report">
          <header>
            <strong>{report.machine}</strong>
            <span>{formatChecklistWhen(report.created_at)}</span>
          </header>
          <p>
            {t("Operador")}: {report.operator_name || report.operator_cpf || "—"}
          </p>
          {report.observation ? (
            <p>
              <strong>{t("Observação")}:</strong> {report.observation}
            </p>
          ) : null}
          {report.observation_audio_url ? (
            <audio controls src={mediaUrl(report.observation_audio_url)} />
          ) : null}
          {report.photos && report.photos.length ? (
            <div className="field-app-checklist-photos">
              {report.photos.map((photo) => (
                <button
                  key={`${report.id}-${photo.item_id}`}
                  type="button"
                  className="field-app-checklist-photo"
                  onClick={() => setPhotoUrl(mediaUrl(photo.url))}
                >
                  <img src={mediaUrl(photo.url)} alt={photo.label} />
                  <span>{photo.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </article>
      ))}
      {photoUrl ? (
        <div
          className="field-app-checklist-lightbox"
          onClick={() => setPhotoUrl("")}
          role="presentation"
        >
          <img src={photoUrl} alt="" />
        </div>
      ) : null}
    </div>
  );
}

export function FieldAppChecklistReportBody({ report, t, onOpenPhoto }) {
  if (!report) return null;
  return (
    <div className="field-app-checklist-report-body">
      <p>
        <strong>{t("Máquina")}:</strong> {report.machine}
      </p>
      <p>
        <strong>{t("Quando")}:</strong> {formatChecklistWhen(report.created_at)}
      </p>
      <p>
        <strong>{t("Operador")}:</strong>{" "}
        {report.operator_name || report.operator_cpf || "—"}
      </p>
      {report.observation ? (
        <p>
          <strong>{t("Observação")}:</strong> {report.observation}
        </p>
      ) : (
        <p className="text-muted">{t("Sem observação escrita.")}</p>
      )}
      {report.observation_audio_url ? (
        <audio controls src={mediaUrl(report.observation_audio_url)} />
      ) : null}
      {report.items && report.items.length ? (
        <ul className="field-app-checklist-item-list">
          {report.items.map((item) => (
            <li key={item.id || item.label}>
              {item.label}
              {item.require_photo ? ` (${t("exigiu foto")})` : ""}
            </li>
          ))}
        </ul>
      ) : null}
      {report.photos && report.photos.length ? (
        <div className="field-app-checklist-photos">
          {report.photos.map((photo) => (
            <button
              key={`${report.id}-${photo.item_id}`}
              type="button"
              className="field-app-checklist-photo"
              onClick={() => onOpenPhoto(mediaUrl(photo.url))}
            >
              <img src={mediaUrl(photo.url)} alt={photo.label} />
              <span>{photo.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function FieldAppChecklistReportModal({ report, onClose, t }) {
  const [photoUrl, setPhotoUrl] = useState("");
  if (!report) return null;
  return (
    <div
      className="field-app-checklist-lightbox"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="field-app-checklist-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="field-app-checklist-modal-header">
          <h3>{t("Checklist ao assumir")}</h3>
          <button type="button" className="btn btn-default btn-sm" onClick={onClose}>
            {t("Fechar")}
          </button>
        </div>
        <FieldAppChecklistReportBody
          report={report}
          t={t}
          onOpenPhoto={setPhotoUrl}
        />
      </div>
      {photoUrl ? (
        <div
          className="field-app-checklist-lightbox"
          onClick={() => setPhotoUrl("")}
          role="presentation"
        >
          <img src={photoUrl} alt="" />
        </div>
      ) : null}
    </div>
  );
}

export default FieldAppChecklistReports;
